import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckSquare, ChevronLeft, Loader2, Square } from "lucide-react";

const BTN = {
  primary: "bg-navy text-white hover:bg-[#1a3a66]",
  secondary: "bg-ocean text-white hover:bg-[#3a9dd6]",
  outline: "bg-transparent text-navy border-[1.5px] border-navy hover:bg-navy/5",
  ghost: "bg-transparent text-navy hover:bg-navy/5",
  glass: "bg-glass-dark text-white border border-glass hover:bg-[rgba(16,39,72,0.7)]",
  light: "bg-white text-navy hover:bg-tert",
};

export function Button({ title, onClick, variant = "primary", icon: Icon, loading, disabled, testId, className = "", type = "button" }) {
  return (
    <button
      type={type}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || loading}
      className={`focus-ring inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full px-6 font-display-bold text-[15px] uppercase tracking-[1.2px] transition-all duration-200 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 ${BTN[variant]} ${className}`}
    >
      {loading ? <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading" /> : (
        <>
          {Icon ? <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden /> : null}
          <span>{title}</span>
        </>
      )}
    </button>
  );
}

export function Chip({ label, selected, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={!!selected}
      onClick={onClick}
      className={`focus-ring h-11 shrink-0 whitespace-nowrap rounded-full border px-4 font-body-semi text-[13px] transition-all duration-150 active:scale-[0.97] ${selected ? "border-navy bg-navy text-white" : "border-line bg-white text-ink hover:border-ocean"}`}
    >
      {label}
    </button>
  );
}

export function OptionRow({ options, value, onChange, multi, testPrefix }) {
  const isSel = (v) => (multi ? (value ?? []).includes(v) : value === v);
  return (
    <div data-testid={`${testPrefix}-group`} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Chip
          key={o.v}
          testId={`${testPrefix}-${o.v}`}
          label={o.l}
          selected={isSel(o.v)}
          onClick={() => {
            if (multi) {
              const arr = (value ?? []).slice();
              onChange(arr.includes(o.v) ? arr.filter((x) => x !== o.v) : [...arr, o.v]);
            } else onChange(value === o.v ? null : o.v);
          }}
        />
      ))}
    </div>
  );
}

export function Tag({ label, tone = "sky" }) {
  const cls = tone === "navy" ? "bg-navy text-white" : tone === "glass" ? "bg-glass-dark text-white" : "bg-sky text-navy";
  return <span className={`inline-flex self-start rounded-full px-2.5 py-1 font-body-semi text-[11px] capitalize ${cls}`}>{label}</span>;
}

export function Card({ children, className = "", testId }) {
  return (
    <div data-testid={testId} className={`shadow-card rounded-[20px] border border-line bg-white p-4 ${className}`}>
      {children}
    </div>
  );
}

export function IconCircle({ icon: Icon, onClick, testId, tone = "light", disabled, label }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`focus-ring flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all active:scale-95 disabled:opacity-40 ${tone === "glass" ? "bg-glass-dark text-white hover:bg-[rgba(16,39,72,0.75)]" : "bg-white text-navy shadow-card hover:bg-tert"}`}
    >
      <Icon className="h-5 w-5" aria-hidden />
    </button>
  );
}

/** Subtle animated wave blobs for the coastal vibe. */
export function WaveBackdrop({ tone = "light" }) {
  const a = tone === "dark" ? "rgba(77,175,232,0.22)" : "rgba(77,175,232,0.18)";
  const b = tone === "dark" ? "rgba(183,216,255,0.12)" : "rgba(183,216,255,0.35)";
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-40 overflow-hidden">
      <div className="animate-wave-b absolute -left-[120px] -right-[120px] -bottom-[260px] h-[360px] rounded-[400px]" style={{ backgroundColor: b }} />
      <div className="animate-wave-a absolute -left-[160px] -right-[80px] -bottom-[300px] h-[380px] rounded-[400px]" style={{ backgroundColor: a }} />
    </div>
  );
}

export function SectionTitle({ title, action, onAction, testId }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="t-h2">{title}</h2>
      {action ? (
        <button type="button" data-testid={testId} onClick={onAction} className="focus-ring min-h-[44px] rounded-md px-1 t-label !text-ocean hover:underline">
          {action}
        </button>
      ) : null}
    </div>
  );
}

export function ConfidenceBar({ value, testId }) {
  const pct = Math.round(value * 100);
  const label = pct >= 75 ? "High" : pct >= 50 ? "Medium" : "Low";
  return (
    <div data-testid={testId} className="flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded bg-tert">
        <div className="h-1.5 rounded bg-ocean transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
      <span className="min-w-[92px] text-right font-body-semi text-xs text-mute">{label} · {pct}%</span>
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`h-6 w-6 animate-spin text-ocean ${className}`} aria-label="Loading" />;
}

export function CheckRow({ checked, onChange, label, testId }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} data-testid={testId} onClick={() => onChange(!checked)} className="focus-ring flex min-h-[44px] w-full items-center gap-2 rounded-md text-left">
      {checked ? <CheckSquare className="h-6 w-6 shrink-0 text-navy" aria-hidden /> : <Square className="h-6 w-6 shrink-0 text-mute" aria-hidden />}
      <span className="t-strong flex-1">{label}</span>
    </button>
  );
}

/** Stack-screen header: back button + centered label (mirrors the app's screen headers). */
export function ScreenHeader({ label, testId, fallback = "/home", right }) {
  const navigate = useNavigate();
  return (
    <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 pb-2 pt-4 md:px-6">
      <IconCircle testId={testId} icon={ChevronLeft} label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(fallback, { replace: true }))} />
      <span className="t-label">{label}</span>
      <div className="flex w-11 justify-end">{right}</div>
    </div>
  );
}

export function Bullets({ items, className = "" }) {
  return (
    <ul className={`mt-2 space-y-1.5 ${className}`}>
      {items.map((s) => (
        <li key={s} className="flex gap-2">
          <span className="font-body-bold text-ocean" aria-hidden>•</span>
          <span className="t-body flex-1 !text-navy">{s}</span>
        </li>
      ))}
    </ul>
  );
}
